package org.moxultimate.topwithmox

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import com.varabyte.kobweb.compose.css.ScrollBehavior
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.compose.ui.modifiers.scrollBehavior
import com.varabyte.kobweb.core.App
import com.varabyte.kobweb.silk.SilkApp
import com.varabyte.kobweb.silk.components.layout.Surface
import com.varabyte.kobweb.silk.init.InitSilk
import com.varabyte.kobweb.silk.init.InitSilkContext
import com.varabyte.kobweb.silk.init.registerStyleBase
import com.varabyte.kobweb.silk.style.common.SmoothColorStyle
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.loadFromLocalStorage
import com.varabyte.kobweb.silk.theme.colors.saveToLocalStorage
import com.varabyte.kobweb.silk.theme.colors.systemPreference
import kotlinx.browser.document
import org.jetbrains.compose.web.css.vh
import org.w3c.dom.HTMLLinkElement
import org.w3c.dom.HTMLMetaElement
import org.w3c.dom.HTMLScriptElement
import org.moxultimate.topwithmox.di.initKoin

private const val COLOR_MODE_KEY = "topwithmox:colorMode"
private const val GA_MEASUREMENT_ID = "G-51576K70P0"

/**
 * initPreconnect
 * Adds preconnect hints so the DNS + TLS handshake for Google Analytics
 * happens in parallel with rendering, shaving ~200ms off first-load GA cost.
 */
@InitSilk
fun initPreconnect(ctx: InitSilkContext) {
    listOf(
        "https://www.googletagmanager.com",
        "https://www.google-analytics.com",
    ).forEach { origin ->
        val link = document.createElement("link") as HTMLLinkElement
        link.rel = "preconnect"
        link.href = origin
        document.head?.appendChild(link)
    }
}

/**
 * initHeroPreload
 * Preloads the above-the-fold hero background image so the browser fetches it
 * immediately, reducing LCP time on the home page.
 */
@InitSilk
fun initHeroPreload(ctx: InitSilkContext) {
    val desktopPreload = document.createElement("link") as HTMLLinkElement
    desktopPreload.rel = "preload"
    desktopPreload.setAttribute("as", "image")
    desktopPreload.href = "/bg_first_section.webp"
    document.head?.appendChild(desktopPreload)

    val mobilePreload = document.createElement("link") as HTMLLinkElement
    mobilePreload.rel = "preload"
    mobilePreload.setAttribute("as", "image")
    mobilePreload.href = "/bg_first_section_mobile.webp"
    document.head?.appendChild(mobilePreload)
}

/**
 * initJsonLd
 * Injects JSON-LD structured data (WebSite + ImageGallery schemas) for Google rich results.
 */
@InitSilk
fun initJsonLd(ctx: InitSilkContext) {
    val script = document.createElement("script") as HTMLScriptElement
    script.type = "application/ld+json"
    script.text = """
        [
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "TopWithMox",
            "url": "https://www.topwithmox.com",
            "description": "Free 4K & 8K wallpapers, latest tech news, product reviews, and top shows — curated weekly.",
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://www.topwithmox.com/wallpaper?q={search_term_string}",
              "query-input": "required name=search_term_string"
            }
          },
          {
            "@context": "https://schema.org",
            "@type": "ImageGallery",
            "name": "TopWithMox Wallpapers",
            "description": "Free 4K & 8K wallpapers for Phone, Desktop & Widescreen. Anime, cars, nature and more.",
            "url": "https://www.topwithmox.com/wallpaper",
            "author": {
              "@type": "Person",
              "name": "TopWithMox",
              "url": "https://www.topwithmox.com"
            }
          }
        ]
    """.trimIndent()
    document.head?.appendChild(script)
}

/**
 * initSeoHead
 * Injects document title and Open Graph / Twitter Card meta tags into <head> at startup.
 */
@InitSilk
fun initSeoHead(ctx: InitSilkContext) {
    document.title = "TopWithMox — Free 4K & 8K Wallpapers"

    val ogTags = listOf(
        "og:title"       to "TopWithMox — Free 4K & 8K Wallpapers",
        "og:description" to "Free 4K & 8K wallpapers, latest tech news, product reviews, and top shows — all curated weekly by TopWithMox.",
        "og:image"       to "https://www.topwithmox.com/og-preview.png",
        "og:url"         to "https://www.topwithmox.com",
        "og:type"        to "website",
    )
    ogTags.forEach { (property, content) ->
        val meta = document.createElement("meta") as HTMLMetaElement
        meta.setAttribute("property", property)
        meta.content = content
        document.head?.appendChild(meta)
    }

    val twitterTags = listOf(
        "twitter:card"  to "summary_large_image",
        "twitter:title" to "TopWithMox — Free 4K & 8K Wallpapers",
        "twitter:image" to "https://www.topwithmox.com/og-preview.png",
    )
    twitterTags.forEach { (name, content) ->
        val meta = document.createElement("meta") as HTMLMetaElement
        meta.name = name
        meta.content = content
        document.head?.appendChild(meta)
    }

    val canonical = document.createElement("link") as HTMLLinkElement
    canonical.rel = "canonical"
    canonical.href = "https://www.topwithmox.com"
    document.head?.appendChild(canonical)
}

@InitSilk
fun initMonetag(ctx: InitSilkContext) {
    val meta = document.createElement("meta") as HTMLMetaElement
    meta.name = "monetag"
    meta.content = "0ce52746b1780c9ff3b10e6abbf7ac76"
    document.head?.appendChild(meta)
}


@InitSilk
fun initGoogleAnalytics(ctx: InitSilkContext) {
    // 1. Load the gtag.js library asynchronously
    val loaderScript = document.createElement("script") as HTMLScriptElement
    loaderScript.async = true
    loaderScript.src = "https://www.googletagmanager.com/gtag/js?id=$GA_MEASUREMENT_ID"
    document.head?.appendChild(loaderScript)

    // 2. Initialize dataLayer and configure the measurement ID
    val initScript = document.createElement("script") as HTMLScriptElement
    initScript.text = """
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '$GA_MEASUREMENT_ID');
    """.trimIndent()
    document.head?.appendChild(initScript)
}


@InitSilk
fun initColorMode(ctx: InitSilkContext) {
    ctx.config.initialColorMode = ColorMode.loadFromLocalStorage(COLOR_MODE_KEY) ?: ColorMode.systemPreference
}

@InitSilk
fun initStyles(ctx: InitSilkContext) {
    ctx.stylesheet.apply {
        registerStyleBase("body") { Modifier.scrollBehavior(ScrollBehavior.Smooth) }
    }
}

@App
@Composable
fun AppEntry(content: @Composable () -> Unit) {
    // Start Koin once before the Compose tree builds — remember ensures it
    // is called exactly once across recompositions.
    remember { initKoin() }

    SilkApp {
        val colorMode = ColorMode.current
        LaunchedEffect(colorMode) {
            colorMode.saveToLocalStorage(COLOR_MODE_KEY)
        }
        Surface(SmoothColorStyle.toModifier().minHeight(100.vh)) {
            content()
        }
    }
}
